/** Helpers for driver login QR print / download sheet */

export function getAbsoluteAssetUrl(relativePath) {
    if (!relativePath) {
        return '';
    }
    if (/^https?:\/\//i.test(relativePath)) {
        return relativePath;
    }
    const base = process.env.PUBLIC_URL || '';
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
    return `${window.location.origin}${base}${path}`;
}

/** Pick first logo path that exists in branding (sfa-logo.png is not deployed). */
export function resolvePosterLogoPath(images = {}) {
    const candidates = [
        images.loginLogo,
        images.sidebarExpanded,
        images.logo,
        '/img/sfa/sda-logo.png',
        '/img/sfa/logo.svg',
    ];
    return candidates.find(Boolean) || '/img/sfa/sda-logo.png';
}

export async function loadPosterLogoDataUrl(images = {}) {
    const paths = [
        images.loginLogo,
        images.sidebarExpanded,
        images.logo,
        '/img/sfa/sda-logo.png',
        '/img/sfa/logo.svg',
    ].filter(Boolean);

    for (const relativePath of paths) {
        try {
            return await loadImageAsDataUrl(getAbsoluteAssetUrl(relativePath));
        } catch {
            // try next candidate
        }
    }
    return null;
}

export function loadImageAsDataUrl(url) {
    return fetch(url)
        .then((res) => {
            if (!res.ok) {
                throw new Error(`Failed to load image: ${url}`);
            }
            return res.blob();
        })
        .then(
            (blob) =>
                new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                }),
        )
        .catch(() => loadImageViaCanvas(url));
}

function loadImageViaCanvas(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = url;
    });
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    let line = '';
    let currentY = y;
    for (let i = 0; i < words.length; i++) {
        const test = line + words[i] + ' ';
        if (ctx.measureText(test).width > maxWidth && i > 0) {
            ctx.fillText(line.trim(), x, currentY);
            line = words[i] + ' ';
            currentY += lineHeight;
        } else {
            line = test;
        }
    }
    ctx.fillText(line.trim(), x, currentY);
    return currentY;
}

export const DRIVER_QR_SHEET_COPY = {
    title: 'Driver Login',
    subtitle:
        'Scan this QR code to open the driver portal, then sign in with your registered mobile number and OTP.',
    footerLine1: 'For authorised drivers only · Keep this poster at your depot',
    footerLine2: (companyName) => `Powered by ${companyName}`,
    qrAlt: 'Driver login QR code',
};

export function buildPortalQrSheetHtml(
    copy,
    {
    logoDataUrl,
    qrDataUrl,
    loginUrl,
    companyFullName,
}) {
    const { title, subtitle, footerLine1, footerLine2, qrAlt = 'Login QR code' } = copy;
    const logoBlock = logoDataUrl
        ? `<img src="${logoDataUrl}" alt="Logo" class="logo" />`
        : '';
    const year = new Date().getFullYear();

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
      color: #111827;
      background: #fff;
      padding: 40px 32px 32px;
      text-align: center;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .header {
      width: 100%;
      max-width: 360px;
      padding-bottom: 20px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 24px;
    }
    .logo {
      max-width: 220px;
      max-height: 64px;
      object-fit: contain;
      margin-bottom: 16px;
    }
    h1 {
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 10px;
      color: #0f172a;
    }
    .subtitle {
      font-size: 14px;
      line-height: 1.5;
      color: #475569;
    }
    .qr-wrap {
      padding: 14px;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      display: inline-block;
      margin-bottom: 16px;
    }
    .qr-wrap img {
      display: block;
      width: 260px;
      height: 260px;
    }
    .url {
      font-family: ui-monospace, monospace;
      font-size: 11px;
      color: #64748b;
      word-break: break-all;
      max-width: 320px;
      margin: 0 auto 32px;
      line-height: 1.4;
    }
    .footer {
      width: 100%;
      max-width: 360px;
      margin-top: auto;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
    }
    .footer p {
      font-size: 12px;
      color: #64748b;
      line-height: 1.5;
      margin-bottom: 6px;
    }
    .footer .brand {
      font-size: 11px;
      color: #94a3b8;
      margin-top: 8px;
    }
    @media print {
      body { padding: 24px; }
      @page { margin: 12mm; }
    }
  </style>
</head>
<body>
  <header class="header">
    ${logoBlock}
    <h1>${title}</h1>
    <p class="subtitle">${subtitle}</p>
  </header>
  <div class="qr-wrap">
    <img src="${qrDataUrl}" alt="${qrAlt}" />
  </div>
  <p class="url">${loginUrl}</p>
  <footer class="footer">
    <p>${footerLine1}</p>
    <p class="brand">${footerLine2(companyFullName)} · © ${year}</p>
  </footer>
</body>
</html>`;
}

export function buildDriverQrSheetHtml(assets) {
    return buildPortalQrSheetHtml(DRIVER_QR_SHEET_COPY, assets);
}

export async function renderPortalQrPosterCanvas(
    copy,
    {
    logoDataUrl,
    qrDataUrl,
    loginUrl,
    companyFullName,
}) {
    const w = 480;
    const h = 720;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);

    let y = 36;

    if (logoDataUrl) {
        try {
            const logoImg = await loadImage(logoDataUrl);
            const maxW = 220;
            const maxH = 56;
            const scale = Math.min(maxW / logoImg.width, maxH / logoImg.height);
            const lw = logoImg.width * scale;
            const lh = logoImg.height * scale;
            ctx.drawImage(logoImg, (w - lw) / 2, y, lw, lh);
            y += lh + 20;
        } catch {
            y += 8;
        }
    }

    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(48, y);
    ctx.lineTo(w - 48, y);
    ctx.stroke();
    y += 28;

    ctx.fillStyle = '#0f172a';
    ctx.font = 'bold 24px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(copy.title, w / 2, y);
    y += 28;

    ctx.fillStyle = '#475569';
    ctx.font = '14px system-ui, sans-serif';
    y = wrapText(ctx, copy.subtitle, w / 2, y, w - 96, 20) + 24;

    if (qrDataUrl) {
        const qrImg = await loadImage(qrDataUrl);
        const qrSize = 260;
        const qrX = (w - qrSize) / 2;
        ctx.strokeStyle = '#e2e8f0';
        ctx.strokeRect(qrX - 14, y - 14, qrSize + 28, qrSize + 28);
        ctx.drawImage(qrImg, qrX, y, qrSize, qrSize);
        y += qrSize + 36;
    }

    ctx.fillStyle = '#64748b';
    ctx.font = '11px ui-monospace, monospace';
    y = wrapText(ctx, loginUrl, w / 2, y, w - 80, 16) + 32;

    ctx.strokeStyle = '#e5e7eb';
    ctx.beginPath();
    ctx.moveTo(48, h - 88);
    ctx.lineTo(w - 48, h - 88);
    ctx.stroke();

    ctx.fillStyle = '#64748b';
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText(copy.footerLine1, w / 2, h - 62);

    ctx.fillStyle = '#94a3b8';
    ctx.font = '11px system-ui, sans-serif';
    const brand = `${copy.footerLine2(companyFullName)} · © ${new Date().getFullYear()}`;
    ctx.fillText(brand, w / 2, h - 40);

    return canvas;
}

export async function renderDriverQrPosterCanvas(assets) {
    return renderPortalQrPosterCanvas(DRIVER_QR_SHEET_COPY, assets);
}

export function printHtmlDocument(html) {
    return new Promise((resolve, reject) => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute(
            'style',
            'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;',
        );
        document.body.appendChild(iframe);

        const win = iframe.contentWindow;
        if (!win) {
            document.body.removeChild(iframe);
            reject(new Error('Print frame unavailable'));
            return;
        }

        const doc = win.document;
        doc.open();
        doc.write(html);
        doc.close();

        const cleanup = () => {
            setTimeout(() => {
                if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                }
                resolve();
            }, 300);
        };

        const triggerPrint = () => {
            try {
                win.focus();
                win.print();
            } catch (err) {
                reject(err);
                return;
            }
            cleanup();
        };

        const images = Array.from(doc.images || []);
        if (images.length === 0) {
            setTimeout(triggerPrint, 150);
            return;
        }

        let settled = 0;
        const onImageDone = () => {
            settled += 1;
            if (settled >= images.length) {
                setTimeout(triggerPrint, 100);
            }
        };

        images.forEach((img) => {
            if (img.complete) {
                onImageDone();
            } else {
                img.onload = onImageDone;
                img.onerror = onImageDone;
            }
        });
    });
}

export function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}

export function downloadCanvasAsPng(canvas, filename) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('Could not create image'));
                return;
            }
            downloadBlob(blob, filename);
            resolve();
        }, 'image/png');
    });
}
